import type { FC, ReactNode } from "react";

import type {
  UsageCostBreakdown,
  UsagePricingDimensions,
} from "../../types/usage";

import { formatTokenCount, toFiniteNumber } from "../../utils/currency";
import { Badge } from "../common/Badge";
import { Text } from "../common/Text";

interface ModalityEntry {
  label: string;
  tokens: number;
}

function formatPricingInputLabel(value: string): string {
  switch (value) {
    case "cacheStorageTokenHours": {
      return "Cache storage token-hours";
    }
    case "completionTokensByModality": {
      return "Output modality split";
    }
    case "input_tokens_total": {
      return "Input threshold tokens";
    }
    case "input_tokens_total_plus_cache": {
      return "Input threshold tokens incl. cache";
    }
    case "uncachedInputTokens": {
      return "Uncached input tokens";
    }
    case "uncachedInputTokensByModality": {
      return "Input modality split";
    }
    default: {
      return value.startsWith("rate:") ? `Rate for ${value.slice(5)}` : value;
    }
  }
}

function modalityLabel(modality: string): string {
  return modality.charAt(0).toUpperCase() + modality.slice(1);
}

function extractModalityEntries(
  value: UsagePricingDimensions["inputTokensByModality"],
): ModalityEntry[] {
  if (!value) {
    return [];
  }

  return Object.entries(value)
    .map(([key, tokens]) => ({
      label: modalityLabel(key),
      tokens: toFiniteNumber(tokens),
    }))
    .filter(
      (entry): entry is ModalityEntry =>
        entry.tokens !== null && entry.tokens > 0,
    )
    .toSorted((a, b) => b.tokens - a.tokens);
}

function CoveragePanel(props: { children: ReactNode; title: string }) {
  return (
    <div className="rounded-none border border-slate-300 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-arti-panel">
      <Text variant="label" as="p">
        {props.title}
      </Text>
      <div className="mt-3">{props.children}</div>
    </div>
  );
}

export function getUsagePricingStatusText(
  costBreakdown: UsageCostBreakdown | undefined,
): null | string {
  const completeness = costBreakdown?.completeness;
  if (completeness === "exact") {
    return "Exact pricing";
  }
  if (completeness === "partial") {
    const missingCount = costBreakdown?.missingPricingInputs?.length ?? 0;
    return missingCount > 0
      ? `Partial pricing · ${missingCount} missing`
      : "Partial pricing";
  }
  return null;
}

export function getMissingPricingInputsTitle(
  missingPricingInputs: string[] | undefined,
): string | undefined {
  if (!missingPricingInputs || missingPricingInputs.length === 0) {
    return undefined;
  }
  return `Missing: ${missingPricingInputs.map(formatPricingInputLabel).join(", ")}`;
}

const ModalityBadgeList: FC<{ entries: ModalityEntry[]; title: string }> = ({
  entries,
  title,
}) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <Text variant="label" as="p" className="text-gray-600 dark:text-gray-300">
        {title}
      </Text>
      <div className="flex flex-wrap gap-1.5">
        {entries.map((entry) => (
          <Badge key={`${title}-${entry.label}`} variant="default" size="sm">
            {entry.label}: {formatTokenCount(entry.tokens)}
          </Badge>
        ))}
      </div>
    </div>
  );
};

export const UsageModalityCoveragePanel: FC<{
  inputTokensByModality: UsagePricingDimensions["inputTokensByModality"];
  outputTokensByModality: UsagePricingDimensions["outputTokensByModality"];
}> = ({ inputTokensByModality, outputTokensByModality }) => {
  const inputEntries = extractModalityEntries(inputTokensByModality);
  const outputEntries = extractModalityEntries(outputTokensByModality);

  if (inputEntries.length === 0 && outputEntries.length === 0) {
    return null;
  }

  return (
    <CoveragePanel title="Modality Splits Used">
      <div className="space-y-3">
        <ModalityBadgeList entries={inputEntries} title="Input" />
        <ModalityBadgeList entries={outputEntries} title="Output" />
      </div>
    </CoveragePanel>
  );
};
